import { api } from '../../function/axios'
import { Ticket, TicketPriority, TicketStatus } from '../../store/support/reducer'
import { EnumTicketStatus } from '../../enum/statusTicket'

/*
 * Tickets for the v2 support desk: the whole list (GET /ticket pages 9 at
 * most, so the first page gives the count and the rest are read a few at a
 * time), the due date as the classic page counts it (from startedTime for
 * `due` days and hours), and labels / colours shared by the queue and the
 * ticket panel.
 */

const SERVER_PAGE = 9
const PARALLEL = 6

export async function loadAllTickets(onProgress?: (loaded: number, total: number) => void): Promise<Ticket[]> {
  const page = async (offset: number) => {
    const { data } = await api({ method: 'GET', url: '/ticket', params: { offset, limit: SERVER_PAGE } })
    return { list: (Array.isArray(data) ? data : data?.list || []) as Ticket[], count: Array.isArray(data) ? null : Number(data?.count) }
  }
  const first = await page(0)
  const total = first.count ?? first.list.length
  const out = [...first.list]
  onProgress?.(out.length, total)
  const offsets: number[] = []
  for (let o = SERVER_PAGE; o < total; o += SERVER_PAGE) offsets.push(o)
  for (let i = 0; i < offsets.length; i += PARALLEL) {
    // eslint-disable-next-line no-await-in-loop
    const chunk = await Promise.all(offsets.slice(i, i + PARALLEL).map(page))
    chunk.forEach((c) => out.push(...c.list))
    onProgress?.(out.length, total)
  }
  // a ticket can shift between pages while they are read — keep one of each
  const seen = new Set<number>()
  return out.filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)))
}

export const STATUSES: TicketStatus[] = [EnumTicketStatus.OPEN, EnumTicketStatus.IN_PROGRESS, EnumTicketStatus.CLOSED]
export const PRIORITIES: TicketPriority[] = ['high', 'medium', 'low']

export const STATUS_LABEL: Record<string, string> = { open: 'Open', 'in-progress': 'In progress', closed: 'Closed' }
export const STATUS_TONE: Record<string, string> = { open: 'var(--danger)', 'in-progress': 'var(--primary)', closed: 'var(--text3)' }
export const PRIORITY_LABEL: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low' }
export const PRIORITY_TONE: Record<string, string> = { high: 'var(--danger)', medium: 'var(--warn)', low: 'var(--text3)' }

/* react-color keeps a type's colour as a hex string or an {r,g,b} / {hex} object */
export function typeColor(c: unknown): string {
  if (typeof c === 'string') return c
  const o = c as { hex?: string; r?: number; g?: number; b?: number } | null
  if (o?.hex) return o.hex
  if (o && Number.isFinite(o.r)) return `rgb(${o.r},${o.g},${o.b})`
  return 'var(--text3)'
}

export const personName = (p?: { firstname?: string | null; lastname?: string | null; email?: string | null } | null) =>
  (p ? [p.firstname, p.lastname].filter(Boolean).join(' ') || p.email || '' : '')

/** when the ticket is due (ms), or null when no due is set or it has not started */
export function dueAt(t: Ticket): number | null {
  if (!t.due || !t.startedTime) return null
  const start = new Date(t.startedTime).getTime()
  if (Number.isNaN(start)) return null
  return start + ((Number(t.due.days) || 0) * 24 + (Number(t.due.hours) || 0)) * 3600000
}

export const isOverdue = (t: Ticket, now = Date.now()) => t.status !== EnumTicketStatus.CLOSED && (dueAt(t) ?? Infinity) < now

function span(ms: number): string {
  const m = Math.round(Math.abs(ms) / 60000)
  if (m < 60) return `${Math.max(1, m)}m`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ${m % 60 ? `${m % 60}m` : ''}`.trim()
  return `${Math.round(h / 24)}d`
}

/** "3h 20m left", "overdue 2d", or null */
export function dueLabel(t: Ticket, now = Date.now()): { text: string; late: boolean } | null {
  if (t.status === EnumTicketStatus.CLOSED) return null
  const at = dueAt(t)
  if (at === null) return null
  return at >= now ? { text: `${span(at - now)} left`, late: false } : { text: `overdue ${span(now - at)}`, late: true }
}

export function ago(iso: string, now = Date.now()): string {
  const ms = now - new Date(iso).getTime()
  if (Number.isNaN(ms)) return ''
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
